import { useState, type ReactNode } from 'react';
import { Button, Input, toast } from '@/components/ui';
import { TextArea } from '@/features/taxInvoices/components/TextArea';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  CASH_RECEIPT_WRITE_ROLES,
  checkOrderForCashReceipt,
  useCashReceiptMutations,
  type CashReceiptTaxType,
  type ManualIssueInput,
  type OrderCheckResult,
} from '../api/cashReceiptApi';
import { isValidBusinessNo, isValidEmail, isValidPhone, splitAmount, useCashReceiptLabels, won } from '../lib/cashReceiptLib';

/*
 * 현금영수증 개별발급(고도몰 같은 이름 화면) — 주문서가 아닌 개별 발급 요청. 주문번호는 선택이고, 넣으면 유효성 검사로
 * 주문을 확인하고 신청자·상품명·금액을 채운다. 공급액·부가세·면세는 발행액에서 자동 계산(복합과세만 면세액 직접 입력).
 */

const GOODS_NAME_MAX = 250;

interface FormState {
  orderNo: string;
  requesterName: string;
  goodsName: string;
  phone: string;
  email: string;
  taxType: CashReceiptTaxType;
  amount: string;
  taxFree: string;
  receiptType: 'INCOME_DEDUCTION' | 'EXPENSE_PROOF';
  identityType: 'PHONE' | 'BUSINESS_NO';
  identityNumber: string;
  adminMemo: string;
}

const EMPTY: FormState = {
  orderNo: '',
  requesterName: '',
  goodsName: '',
  phone: '',
  email: '',
  taxType: 'TAXABLE',
  amount: '',
  taxFree: '',
  receiptType: 'INCOME_DEDUCTION',
  identityType: 'PHONE',
  identityNumber: '',
  adminMemo: '',
};

interface Props {
  /** 발급 후 목록으로. */
  onIssued?: () => void;
}

export const CashReceiptRegisterTab = ({ onIssued }: Props) => {
  const L = useCashReceiptLabels();
  const { t } = L;
  const canWrite = useHasRole(CASH_RECEIPT_WRITE_ROLES);
  const { manualIssue } = useCashReceiptMutations();
  const [f, setF] = useState<FormState>(EMPTY);
  const [checked, setChecked] = useState<OrderCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const patch = (p: Partial<FormState>) => setF((prev) => ({ ...prev, ...p }));

  const amount = Number(f.amount || 0);
  const split = splitAmount(amount, f.taxType, Number(f.taxFree || 0));

  const runCheck = async () => {
    if (!f.orderNo.trim()) return;
    setChecking(true);
    setCheckError(null);
    setChecked(null);
    try {
      const r = await checkOrderForCashReceipt(f.orderNo);
      if (!r.eligible) {
        setCheckError(r.reason ?? t('orders.cashReceiptAdmin.register.notEligible', '현금영수증을 신청할 수 없는 주문입니다.'));
        return;
      }
      setChecked(r);
      // 비어 있는 칸만 주문 정보로 채운다(운영자가 먼저 넣은 값은 그대로).
      setF((prev) => ({
        ...prev,
        requesterName: prev.requesterName || r.requesterName,
        goodsName: prev.goodsName || r.goodsName.slice(0, GOODS_NAME_MAX),
        phone: prev.phone || r.phone,
        email: prev.email || r.email,
        amount: prev.amount || (r.amount > 0 ? String(r.amount) : ''),
        taxType: r.taxType,
        taxFree: r.taxType === 'MIXED' ? String(r.taxFreeAmount) : prev.taxFree,
      }));
    } catch (e) {
      const err = e as { status?: number };
      setCheckError(
        err.status === 404
          ? t('orders.cashReceiptAdmin.register.orderNotFound', '주문을 찾을 수 없거나, 서버 반영 전입니다.')
          : friendlyMessage(e, t('orders.cashReceiptAdmin.register.checkFail', '주문을 확인하지 못했습니다.')),
      );
    } finally {
      setChecking(false);
    }
  };

  const validate = (): Partial<Record<keyof FormState, string>> => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (f.orderNo.trim() && checked?.orderNo !== f.orderNo.trim()) e.orderNo = t('orders.cashReceiptAdmin.register.err.orderCheck', '주문번호 유효성 검사를 해 주세요.');
    if (!f.requesterName.trim()) e.requesterName = t('orders.cashReceiptAdmin.register.err.required', '필수 입력입니다.');
    if (!f.goodsName.trim()) e.goodsName = t('orders.cashReceiptAdmin.register.err.required', '필수 입력입니다.');
    if (!isValidPhone(f.phone)) e.phone = t('orders.cashReceipt.error.phone', '휴대폰번호를 확인해 주세요.');
    if (!isValidEmail(f.email)) e.email = t('orders.cashReceiptAdmin.register.err.email', '이메일을 확인해 주세요.');
    if (!(amount > 0)) e.amount = t('orders.cashReceiptAdmin.register.err.amount', '발행액을 입력해 주세요.');
    if (f.receiptType === 'INCOME_DEDUCTION' && f.identityType !== 'PHONE') {
      e.identityNumber = t('orders.cashReceipt.error.incomeNeedsPhone', '소득공제는 휴대폰번호로만 발급할 수 있습니다.');
    } else if (f.identityType === 'PHONE' ? !isValidPhone(f.identityNumber) : !isValidBusinessNo(f.identityNumber)) {
      e.identityNumber =
        f.identityType === 'PHONE'
          ? t('orders.cashReceipt.error.phone', '휴대폰번호를 확인해 주세요.')
          : t('orders.cashReceipt.error.businessNo', '사업자등록번호 10자리를 확인해 주세요.');
    }
    return e;
  };

  const submit = () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    const input: ManualIssueInput = {
      orderNo: f.orderNo.trim() || null,
      requesterName: f.requesterName.trim(),
      goodsName: f.goodsName.trim(),
      phone: f.phone.replace(/\D/g, ''),
      email: f.email.trim(),
      taxType: f.taxType,
      amount,
      ...split,
      receiptType: f.receiptType,
      identityType: f.identityType,
      identityNumber: f.identityNumber.replace(/\D/g, ''),
      adminMemo: f.adminMemo.trim(),
    };
    manualIssue.mutate(input, {
      onSuccess: () => {
        toast.success(t('orders.cashReceipt.toast.issued', '현금영수증 발급을 요청했습니다.'));
        setF(EMPTY);
        setChecked(null);
        onIssued?.();
      },
      onError: (err) =>
        toast.error(
          err.status === 404
            ? t('orders.cashReceiptAdmin.notDeployedShort', '서버 반영 후 사용할 수 있습니다.')
            : friendlyMessage(err, t('orders.cashReceipt.toast.issueFail', '현금영수증 발급에 실패했습니다.')),
        ),
    });
  };

  const row = (label: string, required: boolean, body: ReactNode, error?: string) => (
    <>
      <span className="pt-2 text-[color:var(--content-muted)]">
        {required ? <span style={{ color: 'var(--status-danger-fg)' }}>• </span> : null}
        {label}
      </span>
      <div className="flex flex-col gap-1">
        {body}
        {error ? <span role="alert" className="text-[12px]" style={{ color: 'var(--status-danger-fg)' }}>{error}</span> : null}
      </div>
    </>
  );

  const radio = <V extends string>(name: string, value: V, current: V, label: string, onPick: (v: V) => void) => (
    <label key={value} className="inline-flex items-center gap-1.5">
      <input type="radio" name={name} checked={current === value} onChange={() => onPick(value)} />
      {label}
    </label>
  );

  return (
    <form
      className="flex flex-col gap-5 text-sm"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      data-testid="cash-receipt-register"
    >
      <section className="rounded-lg p-4" style={{ border: '1px solid var(--divider-strong)' }}>
        <h3 className="mb-3 text-[15px] font-semibold">{t('orders.cashReceiptAdmin.register.info', '현금영수증 정보')}</h3>
        <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-3">
          {row(t('orders.cashReceiptAdmin.register.pg', 'PG 업체'), false, <span className="pt-2">{t('orders.cashReceiptAdmin.register.pgName', '토스페이먼츠')}</span>)}

          {row(
            t('orders.cashReceiptAdmin.register.orderNo', '주문번호'),
            false,
            <div className="flex flex-wrap items-center gap-2">
              <Input className="w-56" value={f.orderNo} onChange={(e) => { patch({ orderNo: e.target.value }); setChecked(null); setCheckError(null); }} data-testid="cash-receipt-register-order-no" />
              <Button type="button" size="sm" variant="outline" disabled={!f.orderNo.trim() || checking} onClick={() => void runCheck()} data-testid="cash-receipt-register-check">
                {t('orders.cashReceiptAdmin.register.check', '유효성 검사')}
              </Button>
              {checked ? <span style={{ color: 'var(--status-success-fg)' }} data-testid="cash-receipt-register-checked">{t('orders.cashReceiptAdmin.register.eligible', '신청 가능')}</span> : null}
              {checkError ? <span role="alert" style={{ color: 'var(--status-danger-fg)' }}>{checkError}</span> : null}
            </div>,
            errors.orderNo,
          )}

          {row(t('orders.cashReceiptAdmin.register.requesterName', '신청자명'), true, <Input className="max-w-xs" value={f.requesterName} onChange={(e) => patch({ requesterName: e.target.value })} data-testid="cash-receipt-register-name" />, errors.requesterName)}

          {row(
            t('orders.cashReceiptAdmin.register.goodsName', '상품명'),
            true,
            <div className="flex items-center gap-2">
              <Input className="max-w-md" value={f.goodsName} maxLength={GOODS_NAME_MAX} onChange={(e) => patch({ goodsName: e.target.value })} data-testid="cash-receipt-register-goods" />
              <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>{f.goodsName.length} / {GOODS_NAME_MAX}</span>
            </div>,
            errors.goodsName,
          )}

          {row(t('orders.cashReceiptAdmin.register.phone', '휴대폰 번호'), true, <Input className="max-w-xs" inputMode="tel" value={f.phone} onChange={(e) => patch({ phone: e.target.value })} data-testid="cash-receipt-register-phone" />, errors.phone)}

          {row(t('orders.cashReceiptAdmin.register.email', '이메일'), true, <Input className="max-w-xs" type="email" value={f.email} onChange={(e) => patch({ email: e.target.value })} data-testid="cash-receipt-register-email" />, errors.email)}

          {row(
            t('orders.cashReceiptAdmin.register.taxType', '과세/면세 여부'),
            true,
            <div className="flex flex-wrap gap-4 pt-2">
              {(['TAXABLE', 'FREE', 'MIXED'] as const).map((v) => radio('cr-tax-type', v, f.taxType, L.taxType(v), (x) => patch({ taxType: x })))}
            </div>,
          )}

          {row(
            t('orders.cashReceiptAdmin.register.amount', '신청 금액'),
            true,
            <div className="grid max-w-sm grid-cols-[70px_1fr] items-center gap-2" data-testid="cash-receipt-register-amounts">
              <span>{t('orders.cashReceiptAdmin.register.total', '발행액')}</span>
              <Input inputMode="numeric" value={f.amount} onChange={(e) => patch({ amount: e.target.value.replace(/\D/g, '') })} data-testid="cash-receipt-register-amount" />
              <span>{t('orders.cashReceiptAdmin.register.supply', '공급액')}</span>
              <Input value={split.supplyAmount.toLocaleString('ko-KR')} readOnly disabled data-testid="cash-receipt-register-supply" />
              <span>{t('orders.cashReceiptAdmin.register.vat', '부가세')}</span>
              <Input value={split.vatAmount.toLocaleString('ko-KR')} readOnly disabled data-testid="cash-receipt-register-vat" />
              <span>{t('orders.cashReceiptAdmin.register.free', '면세')}</span>
              {f.taxType === 'MIXED' ? (
                <Input inputMode="numeric" value={f.taxFree} onChange={(e) => patch({ taxFree: e.target.value.replace(/\D/g, '') })} data-testid="cash-receipt-register-free" />
              ) : (
                <Input value={split.taxFreeAmount.toLocaleString('ko-KR')} readOnly disabled data-testid="cash-receipt-register-free" />
              )}
            </div>,
            errors.amount,
          )}

          {row(
            t('orders.cashReceiptAdmin.register.receiptType', '발행 용도'),
            true,
            <div className="flex flex-wrap gap-4 pt-2">
              {radio('cr-receipt-type', 'INCOME_DEDUCTION' as const, f.receiptType, L.receiptType('INCOME_DEDUCTION'), (x) => patch({ receiptType: x, identityType: 'PHONE' }))}
              {radio('cr-receipt-type', 'EXPENSE_PROOF' as const, f.receiptType, L.receiptType('EXPENSE_PROOF'), (x) => patch({ receiptType: x }))}
            </div>,
          )}

          {row(
            t('orders.cashReceiptAdmin.register.identityType', '인증 종류'),
            true,
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-4 pt-2">
                {radio('cr-identity-type', 'PHONE' as const, f.identityType, L.identityType('PHONE'), (x) => patch({ identityType: x }))}
                {f.receiptType === 'EXPENSE_PROOF'
                  ? radio('cr-identity-type', 'BUSINESS_NO' as const, f.identityType, L.identityType('BUSINESS_NO'), (x) => patch({ identityType: x }))
                  : null}
              </div>
              <div className="flex items-center gap-2">
                <span>{L.identityType(f.identityType)} :</span>
                <Input className="w-56" inputMode="numeric" value={f.identityNumber} onChange={(e) => patch({ identityNumber: e.target.value })} data-testid="cash-receipt-register-identity" />
              </div>
            </div>,
            errors.identityNumber,
          )}
        </div>
      </section>

      <section className="rounded-lg p-4" style={{ border: '1px solid var(--divider-strong)' }}>
        <h3 className="mb-3 text-[15px] font-semibold">{t('orders.cashReceiptAdmin.register.memo', '관리자 메모')}</h3>
        <TextArea value={f.adminMemo} onChange={(e) => patch({ adminMemo: e.target.value })} rows={4} maxLength={1000} aria-label={t('orders.cashReceiptAdmin.register.memo', '관리자 메모')} />
      </section>

      <div className="flex items-center justify-between">
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          {amount > 0 ? t('orders.cashReceiptAdmin.register.summary', { defaultValue: '발행액 {{amount}}', amount: won(amount) }) : ''}
        </span>
        {canWrite ? (
          <Button type="submit" variant="danger" disabled={manualIssue.isPending} data-testid="cash-receipt-register-submit">
            {t('orders.cashReceiptAdmin.register.submit', '현금영수증 개별발급')}
          </Button>
        ) : null}
      </div>
    </form>
  );
};
